import { LitElement, css, html } from 'lit';
import { customElement, property, query } from 'lit/decorators';

import { Menu } from '@material/mwc-menu';
import { StateController } from '../../helpers/state/state-management/state-controller';
import accountInfo from '../../helpers/state/account-info';
import featuresInfo from '../../helpers/state/features-info';
import { logWithAmplitude } from '../../helpers/api/api';
import organizationInfo from '../../helpers/state/organization-info';

@customElement('topic-menu')
export class TopicMenu extends LitElement {
    static amplitude_callsite: string = 'Topic Menu';



    @query('#cornerMenu')
    cornerMenu!: Menu;

    @query('#menuConnect')
    menuConnect!: HTMLDivElement;

    @property({ type: Boolean })
    isOwner: boolean = false;

    @property({ type: Boolean })
    isWriter: boolean = false;

    @property({ type: Boolean })
    showRemoveFromFolder: boolean = false;

    private readonly bindAccountState = new StateController(this, accountInfo);

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        this.cornerMenu.anchor = this.menuConnect;
    }

    private openMenu() {
        // send event to parent to render modals
        this.dispatchEvent(new CustomEvent('open-menu'));
        this.cornerMenu.open = true;
    }

    protected render() {
        return html`
            <div style="position: relative">
                <div id="menuConnect" class="flexContainer">
                    <mwc-icon class="menuIcon" @click=${this.openMenu}>
                        more_horiz
                    </mwc-icon>
                </div>
                <mwc-menu id="cornerMenu" fixed .anchor=${this.menuConnect} x="0" y="30">
                    <mwc-list-item @click="${this.shareClick}">
                        <div class="flexContainer">
                            <mwc-icon class="menuIcon">link</mwc-icon>
                            <span>Share</span>
                        </div>
                    </mwc-list-item>
                    ${organizationInfo.allOrganizations.length > 0 && this.isOwner ? html`
                        <mwc-list-item @click="${this.moveTopicClick}">
                            <div class="flexContainer">
                                <mwc-icon class="menuIcon">input</mwc-icon>
                                <span>Move</span>
                            </div>
                        </mwc-list-item>
                        ` : ''}
                    ${this.isOwner
                        ? html`
                              <mwc-list-item @click="${this.addToFolderClick}">
                                  <div class="flexContainer">
                                      <mwc-icon class="menuIcon">drive_file_move</mwc-icon>
                                      <span>Add to Folder</span>
                                  </div>
                              </mwc-list-item>
                          `
                        : ''}
                    ${this.showRemoveFromFolder && this.isWriter
                        ? html`
                              <mwc-list-item @click="${this.removeFromFolderClick}">
                                  <div class="flexContainer">
                                      <mwc-icon class="menuIcon">folder_delete</mwc-icon>
                                      <span>Remove from Folder</span>
                                  </div>
                              </mwc-list-item>
                          `
                        : ''}
                    ${this.isOwner
                        ? html`
                              <mwc-list-item @click="${this.duplicateTopicClick}">
                                  <div class="flexContainer">
                                        ${featuresInfo.duplicateMapEnabled ? html`
                                            <mwc-icon class="menuIcon">control_point_duplicate</mwc-icon>
                                        ` : html`
                                            <img class="menuPng" src="/images/tiers/pro.png" />
                                        `}
                                      <span>Duplicate</span>
                                  </div>
                              </mwc-list-item>
                              <mwc-list-item @click="${this.deleteTopicClick}">
                                  <div class="flexContainer">
                                      <mwc-icon class="menuIcon">delete</mwc-icon>
                                      <span>Delete Map</span>
                                  </div>
                              </mwc-list-item>
                          `
                        : ''}
                </mwc-menu>
            </div>
        `;
    }

    static get styles() {
        return css`
            :host {
                --mdc-menu-min-width: min(100vw, 232px);
                --mdc-typography-subtitle1-font-size: 12px;
                --context-menu-color: var(--theme-input-color);
            }

            .menuIcon {
                margin-right: 0.5rem;
                color: var(--context-menu-color);
                cursor: pointer;
            }

            .menuPng {
                margin-right: 0.25rem;
                height: 20px;
                margin-top: auto;
                color: var(--context-menu-color);
                cursor: pointer;
            }

            mwc-list-item {
                --mdc-menu-item-height: 2.5rem;
                color: var(--context-menu-color);
            }

            mwc-list-item:hover {
                --context-menu-color: var(--main-primary-color);
                font-weight: 500;
            }

            .flexContainer {
                display: flex;
                align-items: center;
            }

            @media only screen and (max-width: 550px) {
                .menuPng {
                    margin-top: 20px;
                }
            }
        `;
    }

    private shareClick() {
        logWithAmplitude(TopicMenu.amplitude_callsite, 'Share Topic Click');
        this.dispatchEvent(
            new CustomEvent('share-topic', {
                detail: 'share-topic',
            })
        );
    }

    private moveTopicClick() {
        logWithAmplitude(TopicMenu.amplitude_callsite, 'Move Topic Click');
        this.dispatchEvent(
            new CustomEvent('move-topic', {
                detail: 'move-topic',
            })
        );
    }

    private addToFolderClick() {
        logWithAmplitude(TopicMenu.amplitude_callsite, 'Add to Folder Click');
        this.dispatchEvent(
            new CustomEvent('add-to-folder', {
                detail: 'add-to-folder',
            })
        );
    }

    private removeFromFolderClick() {
        logWithAmplitude(TopicMenu.amplitude_callsite, 'Remove from Folder Click');
        this.dispatchEvent(
            new CustomEvent('remove-from-folder', {
                detail: 'remove-from-folder',
            })
        );
    }

    private duplicateTopicClick() {
        logWithAmplitude(TopicMenu.amplitude_callsite, 'Duplicate Topic Click');
        this.dispatchEvent(
            new CustomEvent('duplicate-topic', {
                detail: 'duplicate-topic',
            })
        );
    }

    private deleteTopicClick() {
        logWithAmplitude(TopicMenu.amplitude_callsite, 'Delete Topic Click');
        this.dispatchEvent(
            new CustomEvent('delete-topic', {
                detail: 'delete-topic',
            })
        );
    }
}
